import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FONT, COLOURS } from '../../constants/theme';
import Dialog      from '../ui/Dialog';
import PixelWindow from '../ui/PixelWindow';

/**
 * Validates the RSVP form fields.
 * Returns an object mapping field name → error string.
 * An empty object means the form is valid.
 *
 * @param {object} fields - Form field values.
 * @returns {object} Validation errors.
 */
function validate({ name, email }) {
  const errors = {};

  if (!name.trim()) {
    errors.name = 'Please enter your name.';
  }

  if (!email.trim()) {
    errors.email = 'Please enter your email address.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = 'Please enter a valid email address.';
  }

  return errors;
}

/**
 * RsvpForm — handles guest RSVP collection.
 *
 * ─── BACKEND INTEGRATION NOTE ────────────────────────────────────
 * The `handleSubmit` function currently updates local UI state only.
 * To send data to a backend, replace the TODO block with a fetch call:
 *
 *   const res = await fetch('/api/rsvp', {
 *     method:  'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body:    JSON.stringify(formData),
 *   });
 *
 * Ensure the server also validates all fields and enforces rate limiting
 * to prevent form spam. Never trust client-side validation alone.
 * ─────────────────────────────────────────────────────────────────
 *
 * @param {object}   props
 * @param {string}   props.venue        - Display name shown in the dialog heading.
 * @param {string}   props.accentColour - Primary button / highlight colour.
 */
function RsvpForm({ venue, accentColour }) {
  const [fields, setFields] = useState({
    name:      '',
    email:     '',
    attending: null,   // true | false | null (not yet answered)
    dietary:   '',
    message:   '',
  });

  const [errors,     setErrors]     = useState({});
  const [submitted,  setSubmitted]  = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /** Update a single form field. */
  const setField = (key) => (e) =>
    setFields((prev) => ({ ...prev, [key]: e.target.value }));

  /** Toggle attending status. */
  const setAttending = (value) =>
    setFields((prev) => ({ ...prev, attending: value }));

  /** Submit handler — validates, then posts (or logs) the RSVP. */
  const handleSubmit = async () => {
    const validationErrors = validate(fields);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    try {
      // TODO: replace with real API call — see JSDoc above.
      await new Promise((resolve) => setTimeout(resolve, 400)); // simulated latency
      console.info('[RsvpForm] RSVP submitted', fields);
      setSubmitted(true);
    } catch (err) {
      console.error('[RsvpForm] Submission failed', err);
      setErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Shared styles ── */
  const inputStyle = {
    width:        '100%',
    background:   COLOURS.creamDark,
    border:       `2px solid ${COLOURS.borderGold}`,
    color:        COLOURS.textDark,
    fontFamily:   FONT,
    fontSize:     7,
    padding:      8,
    outline:      'none',
    marginBottom: 4,
    display:      'block',
  };

  const labelStyle = {
    fontFamily:   FONT,
    fontSize:     7,
    color:        COLOURS.textMid,
    display:      'block',
    marginBottom: 4,
    marginTop:    8,
  };

  const errorStyle = {
    fontFamily:   FONT,
    fontSize:     6,
    color:        '#cc2222',
    marginBottom: 8,
  };

  return (
    <div
      style={{ padding: '10px 12px', overflowY: 'auto', maxHeight: '60vh' }}
    >
      <Dialog name={`🌸 ${venue} RSVP`}>
        &quot;Under autumn leaves and lantern light, we&apos;d love for you to join
        our celebration.&quot;
        <span className="cursor">█</span>
      </Dialog>

      <PixelWindow title="✿ YOUR DETAILS">
        {/* Name */}
        <label style={labelStyle} htmlFor="rsvp-name">YOUR NAME *</label>
        <input
          id="rsvp-name"
          style={inputStyle}
          type="text"
          autoComplete="name"
          placeholder="Enter your name..."
          value={fields.name}
          onChange={setField('name')}
          maxLength={100}   // prevent excessively long input
          aria-required="true"
          aria-describedby={errors.name ? 'err-name' : undefined}
        />
        {errors.name && <div id="err-name" style={errorStyle} role="alert">{errors.name}</div>}

        {/* Email */}
        <label style={labelStyle} htmlFor="rsvp-email">EMAIL *</label>
        <input
          id="rsvp-email"
          style={inputStyle}
          type="email"
          autoComplete="email"
          placeholder="your@email.com"
          value={fields.email}
          onChange={setField('email')}
          maxLength={254}   // RFC 5321 max email length
          aria-required="true"
          aria-describedby={errors.email ? 'err-email' : undefined}
        />
        {errors.email && <div id="err-email" style={errorStyle} role="alert">{errors.email}</div>}

        {/* Attending toggle */}
        <label style={labelStyle}>WILL YOU JOIN US?</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
          <button
            type="button"
            onClick={() => setAttending(true)}
            aria-pressed={fields.attending === true}
            style={{
              fontFamily:  FONT,
              fontSize:    6,
              color:       COLOURS.textInverted,
              background:  fields.attending === true ? accentColour : COLOURS.textMid,
              border:      `3px solid rgba(0,0,0,0.2)`,
              padding:     '10px 6px',
              cursor:      'pointer',
              boxShadow:   '3px 3px 0 rgba(0,0,0,0.2)',
              lineHeight:  1.8,
            }}
          >
            ✿ YES!{' '}
            <span style={{ fontSize: 5, display: 'block' }}>I&apos;ll be there!</span>
          </button>
          <button
            type="button"
            onClick={() => setAttending(false)}
            aria-pressed={fields.attending === false}
            style={{
              fontFamily:  FONT,
              fontSize:    6,
              color:       '#f0ffe8',
              background:  fields.attending === false ? COLOURS.sage : COLOURS.textMid,
              border:      `3px solid ${COLOURS.sageDark}`,
              padding:     '10px 6px',
              cursor:      'pointer',
              boxShadow:   `3px 3px 0 ${COLOURS.sageDeep}`,
              lineHeight:  1.8,
            }}
          >
            ✕ REGRETS{' '}
            <span style={{ fontSize: 5, display: 'block' }}>Can&apos;t make it</span>
          </button>
        </div>

        {/* Dietary */}
        <label style={labelStyle} htmlFor="rsvp-dietary">DIETARY NOTES</label>
        <input
          id="rsvp-dietary"
          style={inputStyle}
          type="text"
          placeholder="Any allergies or needs..."
          value={fields.dietary}
          onChange={setField('dietary')}
          maxLength={200}
        />

        {/* Message */}
        <label style={labelStyle} htmlFor="rsvp-message">MESSAGE FOR THE COUPLE</label>
        <textarea
          id="rsvp-message"
          style={{ ...inputStyle, height: 60, resize: 'vertical' }}
          placeholder="Leave a little note..."
          value={fields.message}
          onChange={setField('message')}
          maxLength={500}
        />

        {/* Submission error */}
        {errors.submit && (
          <div style={errorStyle} role="alert">{errors.submit}</div>
        )}

        {/* Submit */}
        <button
          type="button"
          disabled={submitting}
          onClick={handleSubmit}
          style={{
            width:       '100%',
            fontFamily:  FONT,
            fontSize:    7,
            color:       COLOURS.textInverted,
            background:  submitting ? COLOURS.textMid : accentColour,
            border:      `3px solid rgba(0,0,0,0.2)`,
            padding:     10,
            cursor:      submitting ? 'not-allowed' : 'pointer',
            boxShadow:   '3px 3px 0 rgba(0,0,0,0.25)',
            marginTop:   4,
          }}
        >
          {submitting ? 'SENDING…' : '✿ SEND WITH LOVE ✿'}
        </button>
      </PixelWindow>

      {/* Success state */}
      {submitted && (
        <Dialog name="🐾 MATCHA">
          &quot;*vigorous tail wagging* RECEIVED! See you at the celebration! 🍁&quot;
          <span className="cursor">█</span>
        </Dialog>
      )}
    </div>
  );
}

RsvpForm.propTypes = {
  venue:        PropTypes.string.isRequired,
  accentColour: PropTypes.string.isRequired,
};

export default RsvpForm;
